import React, { useCallback } from 'react'
import { useSelector } from 'react-redux'

import { useBundle } from '@amzn/react-arb-tools'
import Column from '@amzn/meridian/column'
import Divider from '@amzn/meridian/divider'
import Row from '@amzn/meridian/row'
import Text from '@amzn/meridian/text'

/* Monochrome, <= 24x24, `currentColor` fills -- per Meridian's custom-icon guidance. */
import associateEntrySvg from '!svg-inline-loader!@src/assets/icons/associate-entry-icon.svg'
import planogramSvg from '!svg-inline-loader!@src/assets/icons/planogram-icon.svg'
import storeToolsSvg from '!svg-inline-loader!@src/assets/icons/store-tools-icon.svg'
import NavTabIcon from '@components/BottomNav/NavTabIcon'
import { _navigateToTool } from '@components/SideBar/utils/SideBarUtil'
import { getSelectedTool } from '@atredux/selectors/toolSelector'
import * as BarcodeScannerService from '@services/barcode/BarcodeScannerService'
import { getToolNameToDetailMap } from '@services/ToolMenuConfigService'
import { ITool } from '@atredux/slices/toolSlice'
import { BUNDLE_NAME } from '@src/constants'
import { t } from '@src/translationHelper'
import { bottomNavConfig } from '@utils/ui-config'

import './BottomNav.scss'

interface IDirectLinkTab {
    id: string
    /** Tool-registry key, resolved through the same map as the side nav. */
    displayName: string
    labelStringId: string
    svg: string
}

export const DIRECT_LINK_TABS: IDirectLinkTab[] = [
    {
        id: 'planogram',
        displayName: 'Planogram',
        labelStringId: 'menu-subzone-planogram',
        svg: planogramSvg,
    },
    {
        id: 'associate-entry',
        displayName: 'Associate Entry',
        labelStringId: 'jsma-web-bottom-nav-entry-label',
        svg: associateEntrySvg,
    },
]

interface INavTabProps {
    atbId: string
    label: string
    selected: boolean
    disabled?: boolean
    onClick: () => void
    children: React.ReactNode
}

/** A single tab. Native <button>: Meridian has no tab bar, and Button cannot stack icon over label. */
const NavTab = ({
    atbId,
    label,
    selected,
    disabled,
    onClick,
    children,
}: Readonly<INavTabProps>): React.ReactElement => (
    <button
        className={`bottom-nav-tab ${selected ? 'selected' : ''}`}
        data-atb-id={atbId}
        aria-pressed={selected}
        disabled={disabled}
        onClick={onClick}
    >
        <Column
            height="100%"
            spacing="100"
            spacingInset="none 200"
            alignmentHorizontal="center"
            alignmentVertical="center"
        >
            {children}
            <Text type="b100" alignment="center" truncate={true} className="bottom-nav-label">
                {label}
            </Text>
        </Column>
    </button>
)

interface IBottomNavBarProps {
    isToolsPageOpen: boolean
    toggleMenu: () => void
    toggleMenuButton: () => void
    /** Closes the profile and notifications sheets, which render above every tab. */
    dismissOverlaySheets: () => void
}

/** Always-visible handheld navigation. Replaces the meridian side nav. */
const BottomNavBar = ({
    isToolsPageOpen,
    toggleMenu,
    toggleMenuButton,
    dismissOverlaySheets,
}: Readonly<IBottomNavBarProps>): React.ReactElement => {
    const [bundle] = useBundle(BUNDLE_NAME)
    const selectedTool = useSelector(getSelectedTool)

    /* Open-only, not a toggle: re-tapping the active tab leaves the screen alone, as the direct-link
       tabs do. `toggleMenu` is shared with the side nav's hamburger, where toggling is correct. */
    const openToolsPage = useCallback(() => {
        // Unconditional: clearing a sheet that covers the grid is not navigation, so it still
        // holds when the tools tab is already active.
        dismissOverlaySheets()
        BarcodeScannerService.stopScan()
        if (!isToolsPageOpen) {
            toggleMenu()
        }
    }, [dismissOverlaySheets, isToolsPageOpen, toggleMenu])

    const navigateToDirectLink = useCallback((displayName: string, tool?: ITool) => {
        if (!tool) {
            // A tab can outlive its tool: registrations load asynchronously.
            console.warn(`SMP: Bottom nav tab "${displayName}" has no registered tool`)
            return
        }
        dismissOverlaySheets()
        if (isToolsPageOpen) {
            toggleMenu()
        }
        // Assumes the tool takes _navigateToTool's main path, not its seaward/tasks branches.
        _navigateToTool(tool, undefined, toggleMenuButton)
    }, [dismissOverlaySheets, isToolsPageOpen, toggleMenu, toggleMenuButton])

    const toolNameToDetailMap = getToolNameToDetailMap()

    return (
        /* heights keeps the divider at 1px and gives the tab row the rest, so the
           nav measures exactly the BOTTOM_NAV_HEIGHT ToolLayout reserves. */
        <Column
            className="bottom-nav"
            data-atb-id="bottom-nav"
            tag="nav"
            spacing="none"
            spacingInset="none"
            backgroundColor="primary"
            height={bottomNavConfig.BOTTOM_NAV_HEIGHT}
            heights={['fit', 'fill']}
        >
            <Divider aria-hidden={true} />
            <Row widths="fill" spacing="none" alignmentVertical="stretch">
                <NavTab
                    atbId="bottom-nav-tools"
                    label={t(bundle, 'jsma-web-common-storeTools')}
                    selected={isToolsPageOpen}
                    onClick={openToolsPage}
                >
                    <NavTabIcon svg={storeToolsSvg} />
                </NavTab>
                {DIRECT_LINK_TABS.map((tab) => {
                    const tool = toolNameToDetailMap.get(tab.displayName)
                    const isSelected = !isToolsPageOpen && selectedTool?.displayName === tab.displayName
                    return (
                        <NavTab
                            key={tab.id}
                            atbId={`bottom-nav-${tab.id}`}
                            label={t(bundle, tab.labelStringId) || tab.displayName}
                            selected={isSelected}
                            disabled={!tool}
                            onClick={() => navigateToDirectLink(tab.displayName, tool)}
                        >
                            <NavTabIcon svg={tab.svg} />
                        </NavTab>
                    )
                })}
            </Row>
        </Column>
    )
}

export default BottomNavBar
